/**
 * PageScopeContext
 *
 * The single positional answer to "which artifact owns the data this leaf
 * renders". The value is a per-opener Valtio proxy: leaves subscribe with
 * `useSnapshot(useContext(PageScopeContext))` and branch on `source`.
 * The default value is the ambient form scope — the form page mounts no
 * opener; entity scopes and the layout-editor preview override it per
 * subtree, so multiple entity scopes plus the form scope are live at one
 * commit. `formId`/`versionId` are deliberately NOT here — they stay on
 * the `formStore` singleton, the single source of truth leaves already
 * snapshot. `preview` is typed as a valtio `ref` so the mocked store's
 * class instances and writers survive proxying and snapshotting intact.
 * `onSelectRecord` must be read off the bare context proxy at event time,
 * never through a snapshot.
 */
import { createContext } from 'react';
import { proxy, ref } from 'valtio';
import type { UseEntityRecordStoreReturn } from '../entityRecord/useEntityRecordStore';

type PreviewScopeRef = ReturnType<typeof ref<UseEntityRecordStoreReturn>>;

export type PageScope =
    | { source: 'form' }
    | {
          source: 'entity';
          entityId: string;
          recordId: string | undefined;
          onSelectRecord: ((recordId: string) => void) | undefined;
      }
    | { source: 'preview'; preview: PreviewScopeRef };

const FORM_PAGE_SCOPE: PageScope = proxy({ source: 'form' });

export const PageScopeContext = createContext<PageScope>(FORM_PAGE_SCOPE);
